import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  DeviceHealthKind, DeviceHealthProblem, DeviceHealthScope, DeviceHealthSummary, DeviceHealthWindow,
} from '@platform/shared-types';
import { deviceHealthApi, DeviceHealthApiError } from '../../services/deviceHealthApi';
import { RubyLoader } from '../RubyLoader';
import { RubyGemMark } from '../RubyGemMark';
import { buildTiles, sparkPoints, fmtAgo, type HealthTile } from './healthPanelModel';
import { useDeviceHref } from '../../pages/pos/device/deviceLinkBase';

/**
 * THE device-health panel (spec §9) — one component, three homes: Fleet
 * Monitoring (fleet), the device Health tab (device), Releases → Rollout
 * (version). Ruby-on-light; the gem loader only. Layout follows view ① of
 * docs/mockups/device-health-mockup.html.
 *
 * Labels say what the server actually computes:
 *  - the worst-device / worst-store lists are ALWAYS the last 24h, whatever the window;
 *  - the 1h window is the current clock hour, not a rolling 60 minutes;
 *  - open auto-tickets (and top problems) are scoped to the selected window.
 */
const TONE: Record<HealthTile['tone'], string> = {
  good: 'text-emerald-700', warn: 'text-amber-700', bad: 'text-[#DC143C]', neutral: 'text-stone-900',
};
const WINDOWS: DeviceHealthWindow[] = ['1h', '24h', '7d'];
const WINDOW_LABEL: Record<DeviceHealthWindow, string> = { '1h': 'this clock hour', '24h': '24h', '7d': '7d', '30d': '30d' };

/** Series colours — match the approved mockup legend. */
const SERIES = [
  { key: 'server', label: 'Server errors', color: '#d97706', width: 2, dash: undefined },
  { key: 'crashes', label: 'Crashes', color: '#DC143C', width: 2.5, dash: undefined },
  { key: 'printer', label: 'Printer', color: '#78716c', width: 2, dash: '4 3' },
  { key: 'other', label: 'Other', color: '#d6d3d1', width: 1.5, dash: undefined },
] as const;

const KIND_TAG: { test: (k: DeviceHealthKind) => boolean; label: string; cls: string }[] = [
  { test: (k) => k === 'pay.anomaly', label: 'PAYMENT', cls: 'bg-red-50 text-red-700 border border-red-200' },
  { test: (k) => k.startsWith('crash.'), label: 'CRASH', cls: 'bg-[#FFF1F3] text-[#9F1239]' },
  { test: (k) => k === 'net.fail', label: 'SERVER', cls: 'bg-amber-50 text-amber-800' },
  { test: (k) => k === 'print.fail', label: 'PRINTER', cls: 'bg-stone-100 text-stone-700' },
  { test: (k) => k === 'usb.drop', label: 'USB', cls: 'bg-stone-100 text-stone-700' },
  { test: (k) => k.startsWith('js.'), label: 'APP', cls: 'bg-[#FFF1F3] text-[#9F1239]' },
];
const kindTag = (k: DeviceHealthKind) => KIND_TAG.find((t) => t.test(k)) ?? { label: 'WARN', cls: 'bg-stone-100 text-stone-700' };

const H3 = 'mb-2.5 text-[12px] font-semibold uppercase tracking-wide text-stone-500';
const CARD = 'rounded-2xl border border-stone-200 bg-white p-4';

/** Where the collapsible strip remembers whether its details are open. */
export const HEALTH_DETAILS_OPEN_KEY = 'fleet.healthDetails.open';

function readDetailsOpen(): boolean {
  try { return localStorage.getItem(HEALTH_DETAILS_OPEN_KEY) === '1'; } catch { return false; }
}

/**
 * `collapsible` (Fleet Monitoring): one thin strip of stat tiles + the window
 * pills + a "Health details" toggle that reveals the trend, worst stores, top
 * problems and worst devices. Collapsed by default so the device list starts
 * high on the page. Off (default) = the full panel, unchanged.
 */
export default function DeviceHealthPanel({ scope, id, window: initial = '24h', compact = false, collapsible = false, onSummary }: {
  scope: DeviceHealthScope; id?: string; window?: DeviceHealthWindow; compact?: boolean;
  collapsible?: boolean;
  onSummary?: (s: DeviceHealthSummary) => void;
}) {
  const deviceHref = useDeviceHref();
  const [win, setWin] = useState<DeviceHealthWindow>(initial);
  const [summary, setSummary] = useState<DeviceHealthSummary | null>(null);
  const [problems, setProblems] = useState<DeviceHealthProblem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(() => (collapsible ? readDetailsOpen() : false));
  const toggleDetails = () => {
    setDetailsOpen((o) => {
      const next = !o;
      try { localStorage.setItem(HEALTH_DETAILS_OPEN_KEY, next ? '1' : '0'); } catch { /* storage blocked — per-view only */ }
      return next;
    });
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const tenantId = scope === 'tenant' ? id : undefined;
    Promise.all([
      deviceHealthApi.summary({ scope, id, window: win }),
      scope === 'device' || scope === 'version' ? Promise.resolve([] as DeviceHealthProblem[]) : deviceHealthApi.problems({ tenantId, window: win }),
    ])
      .then(([s, p]) => { if (!live) return; setSummary(s); setProblems(p); onSummary?.(s); })
      .catch((e) => {
        if (!live) return;
        setError(e instanceof DeviceHealthApiError && (e.status === 401 || e.status === 403)
          ? 'You need Fleet Monitoring access to see device health. Ask the owner to grant it in Roles & Access.'
          : 'Device health could not be loaded. It is not a sign the devices are healthy.');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, id, win]);

  const winLabel = WINDOW_LABEL[win];
  const showStores = !compact && (scope === 'fleet' || scope === 'version') && (summary?.worstStores.length ?? 0) > 0;
  const showTrend = !compact && (summary?.series.length ?? 0) > 1;

  const pills = (
    <div className="flex gap-2" role="group" aria-label="Time window">
      {WINDOWS.map((w) => (
        <button key={w} type="button" onClick={() => setWin(w)} aria-pressed={w === win}
          className={`min-h-[44px] min-w-[44px] rounded-full px-4 text-[13px] border transition-colors ${w === win
            ? 'bg-[#DC143C] border-[#DC143C] text-white font-semibold'
            : 'bg-white border-stone-300 text-stone-600 hover:bg-stone-50'}`}>
          {w}
        </button>
      ))}
    </div>
  );

  /** Trend, worst stores, top problems, worst devices — shared by both layouts. */
  const renderDetails = (summary: DeviceHealthSummary) => (
    <>
          {(showTrend || showStores) && (
            <div className={`grid gap-3.5 ${showTrend && showStores ? 'lg:grid-cols-[1.4fr_1fr]' : ''}`}>
              {showTrend && (() => {
                const p = sparkPoints(summary.series, 600, 116);
                return (
                  <div className={CARD} data-testid="device-health-trend">
                    <h3 className={H3}>Errors by kind · {winLabel}</h3>
                    <svg viewBox="0 0 600 120" className="h-[120px] w-full" preserveAspectRatio="none" role="img" aria-label={`Errors by kind, ${winLabel}`}>
                      <polyline fill="none" stroke="#e7e5e4" strokeWidth="1" points="0,119 600,119" />
                      {[...SERIES].reverse().map((s) => (
                        <polyline key={s.key} points={p[s.key]} fill="none" stroke={s.color} strokeWidth={s.width}
                          strokeDasharray={s.dash} vectorEffect="non-scaling-stroke" transform="translate(0,2)" />
                      ))}
                    </svg>
                    <div className="mt-1.5 flex flex-wrap gap-3.5 text-[12px] text-stone-500">
                      {SERIES.map((s) => (
                        <span key={s.key} className="inline-flex items-center gap-1.5">
                          <i className="inline-block h-[7px] w-[7px] rounded-full" style={{ background: s.color }} />{s.label}
                        </span>
                      ))}
                      <span>peak {p.max}/h</span>
                    </div>
                  </div>
                );
              })()}

              {showStores && (
                <div className={CARD}>
                  <h3 className={H3}>Worst stores · 24h</h3>
                  <table className="w-full border-collapse text-[13px]">
                    <thead>
                      <tr className="text-left text-[12px] text-stone-500">
                        <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Store</th>
                        <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Crash-free</th>
                        <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.worstStores.slice(0, 5).map((s) => (
                        <tr key={s.tenantId} className="border-b border-stone-100 last:border-0">
                          <td className="px-1.5 py-2">
                            <div className="font-semibold text-stone-900">{s.tenantName || s.tenantId}</div>
                            <div className="text-[12px] text-stone-500">{s.devices} till{s.devices === 1 ? '' : 's'}</div>
                          </td>
                          <td className={`px-1.5 py-2 tabular-nums ${s.crashFreePct !== null && s.crashFreePct < 99 ? 'text-[#DC143C]' : 'text-stone-700'}`}>
                            {s.crashFreePct === null ? '—' : `${s.crashFreePct}%`}
                          </td>
                          <td className="px-1.5 py-2 tabular-nums text-stone-700">{s.errors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {problems.length > 0 && (
            <div className={CARD}>
              <h3 className={H3}>Top problems · {winLabel}</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-[12px] text-stone-500">
                      <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Problem</th>
                      {scope !== 'tenant' && <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Store</th>}
                      <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Count</th>
                      <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Tills</th>
                      <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Last seen</th>
                      <th className="border-b border-stone-200 px-1.5 py-2 font-semibold">Ticket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {problems.slice(0, compact ? 3 : 8).map((p) => {
                      const tag = kindTag(p.kind);
                      return (
                        <tr key={`${p.tenantId}#${p.fingerprint}`} className="border-b border-stone-100 last:border-0">
                          <td className="px-1.5 py-2">
                            <span className={`mr-1.5 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${tag.cls}`}>{tag.label}</span>
                            {p.issueId
                              ? <Link to={`/app-issues/${p.issueId}`} className="inline-flex min-h-[44px] items-center font-semibold text-stone-900 hover:text-[#DC143C]">{p.title}</Link>
                              : <span className="font-semibold text-stone-900">{p.title}</span>}
                          </td>
                          {scope !== 'tenant' && <td className="px-1.5 py-2 text-stone-700">{p.tenantName || p.tenantId}</td>}
                          <td className="px-1.5 py-2 tabular-nums text-stone-700">{p.count}</td>
                          <td className="px-1.5 py-2 tabular-nums text-stone-700">{p.devices}</td>
                          <td className="whitespace-nowrap px-1.5 py-2 text-stone-500">{fmtAgo(p.lastSeen)}</td>
                          <td className="px-1.5 py-2">
                            {p.issueId
                              ? <Link to={`/app-issues/${p.issueId}`} className="inline-flex min-h-[44px] items-center font-semibold text-[#DC143C]">{p.ticketStatus || 'open'}</Link>
                              : <span className="text-stone-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!compact && (
                <Link to="/app-issues" className="mt-2 inline-flex min-h-[44px] items-center text-[13px] font-semibold text-[#DC143C]">
                  All auto-tickets in the Issue Console →
                </Link>
              )}
            </div>
          )}

          {!compact && summary.worstDevices.length > 0 && (
            <div className={CARD}>
              <h3 className={H3}>Worst devices · 24h</h3>
              <ul className="divide-y divide-stone-100">
                {summary.worstDevices.slice(0, 5).map((d) => (
                  <li key={d.deviceId} className="flex min-h-[44px] flex-wrap items-center justify-between gap-2 py-1 text-[13px]">
                    <Link to={deviceHref(d.deviceId, 'health')} className="inline-flex min-h-[44px] items-center font-mono font-semibold text-[#DC143C]">{d.deviceId}</Link>
                    <span className="tabular-nums text-stone-500">{d.errors} errors · {d.crashes} crash{d.crashes === 1 ? '' : 'es'} · last crash {fmtAgo(d.lastCrashAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
    </>
  );

  if (collapsible) {
    const tiles = summary && !error ? buildTiles(summary) : [];
    return (
      <section className="rounded-xl border border-stone-200 bg-white" aria-label="Device health">
        <div className="flex flex-wrap items-stretch">
          {loading && !summary && !error && (
            <div data-testid="device-health-loading" className="flex min-h-[72px] flex-1 items-center px-4">
              <RubyLoader variant="inline" size={20} label="Loading device health…" />
            </div>
          )}
          {error && (
            <div role="alert" className="flex min-h-[72px] flex-1 items-center px-4 text-[13px] text-[#9F1239]">{error}</div>
          )}
          {tiles.map((t) => (
            <div key={t.id} className={`relative grid min-w-0 flex-[1_1_150px] gap-0.5 border-r border-stone-200 px-4 py-3 ${loading ? 'opacity-60' : ''}`}>
              <span className="text-[11.5px] uppercase tracking-wider text-stone-500">{t.label}</span>
              <span className={`text-[20px] font-semibold tabular-nums ${TONE[t.tone]}`}>{t.value}</span>
              <span className="truncate text-[12px] text-stone-500">{t.note ?? winLabel}</span>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 border-t border-stone-200 px-3 py-2 sm:border-t-0 sm:border-l max-sm:w-full">
            {loading && summary && <RubyGemMark size={16} drawing color="#DC143C" title="Refreshing" />}
            {pills}
            <button type="button" onClick={toggleDetails} aria-expanded={detailsOpen} aria-controls="device-health-details"
              disabled={!summary || !!error}
              className="min-h-[44px] rounded-lg px-2.5 text-[13px] font-semibold text-[#DC143C] hover:bg-[#FFF1F3] disabled:opacity-40">
              {detailsOpen ? 'Hide details ▴' : 'Health details ▾'}
            </button>
          </div>
        </div>
        {win === '1h' && <div className="border-t border-stone-200 px-4 py-1.5 text-[12px] text-stone-500">1h shows the current clock hour so far.</div>}
        {detailsOpen && summary && !error && (
          <div id="device-health-details" className="space-y-3.5 border-t border-stone-200 bg-stone-50 p-4">
            {renderDetails(summary)}
            {!showTrend && !showStores && problems.length === 0 && summary.worstDevices.length === 0 && (
              <div className="text-[13px] text-stone-500">Nothing to break down for this window.</div>
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="space-y-3.5" aria-label="Device health">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[14.5px] font-bold tracking-tight text-stone-900">Device health</div>
          {win === '1h' && <div className="text-[12px] text-stone-500">1h shows the current clock hour so far.</div>}
        </div>
        {pills}
      </div>

      {loading && !summary && !error && <div data-testid="device-health-loading"><RubyLoader variant="section" className="py-10" /></div>}
      {error && <div role="alert" className="rounded-xl border border-[#DC143C]/30 bg-[#FFF1F3] p-4 text-[13px] text-[#9F1239]">{error}</div>}

      {summary && !error && (
        <div className={`relative space-y-3.5 ${loading ? 'opacity-60' : ''}`}>
          {loading && <RubyLoader variant="overlay" size={40} />}

          <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
            {buildTiles(summary).map((t) => (
              <div key={t.id} className={CARD}>
                <div className="text-[12px] text-stone-500">{t.label} · {winLabel}</div>
                <div className={`my-0.5 text-[28px] font-extrabold tabular-nums tracking-tight ${TONE[t.tone]}`}>{t.value}</div>
                {t.note && (t.id === 'tickets'
                  ? <span className="inline-flex rounded-full bg-[#FFF1F3] px-2 py-0.5 text-[11px] font-bold text-[#9F1239]">{t.note}</span>
                  : <div className="text-[12px] text-stone-500">{t.note}</div>)}
              </div>
            ))}
          </div>

          {renderDetails(summary)}
        </div>
      )}
    </section>
  );
}
